import { useState } from 'react';
import { ArrowUpCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace, TIER_LIMITS, type SubscriptionTier } from '../contexts/WorkspaceContext';

interface UpgradePromptProps {
  feature: string;
  currentCount: number;
  limit: number;
  compact?: boolean;
}

const TIER_ORDER: SubscriptionTier[] = ['free', 'starter', 'pro', 'business'];

function getNextTier(current: SubscriptionTier, feature: keyof typeof TIER_LIMITS['free'], needed: number): SubscriptionTier {
  const currentIdx = TIER_ORDER.indexOf(current);
  for (let i = currentIdx + 1; i < TIER_ORDER.length; i++) {
    const tierLimits = TIER_LIMITS[TIER_ORDER[i]];
    const limit = tierLimits[feature];
    if (limit === Infinity || limit > needed) {
      return TIER_ORDER[i];
    }
  }
  return 'business';
}

const TIER_DISPLAY: Record<SubscriptionTier, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  business: 'Business',
  admin: 'Admin',
};

export default function UpgradePrompt({ feature, currentCount, limit, compact = false }: UpgradePromptProps) {
  const { checkout, changePlan } = useAuth();
  const { tier, workspace, refreshWorkspace } = useWorkspace();
  const [isChanging, setIsChanging] = useState(false);

  const featureKey = feature === 'downloads' ? 'downloadsPerMonth' : feature as keyof typeof TIER_LIMITS['free'];
  const suggestedTier = getNextTier(tier, featureKey, currentCount);
  const hasSubscription = !!workspace?.polarSubscriptionId;

  const handleUpgrade = async () => {
    if (suggestedTier === 'free') return;

    if (hasSubscription) {
      // Paid user: change plan via API (no redirect)
      setIsChanging(true);
      try {
        const success = await changePlan(suggestedTier);
        if (success) {
          await new Promise(r => setTimeout(r, 1500));
          await refreshWorkspace();
        }
      } finally {
        setIsChanging(false);
      }
    } else {
      // Free user: new checkout session (redirects to Polar)
      checkout(suggestedTier);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        {isChanging ? (
          <Loader2 size={16} className="text-amber-500 shrink-0 animate-spin" />
        ) : (
          <ArrowUpCircle size={16} className="text-amber-500 shrink-0" />
        )}
        <span className="text-xs text-amber-700 dark:text-amber-400">
          {limit === Infinity ? feature : `${currentCount}/${limit} ${feature}`} &mdash;{' '}
          <button
            onClick={handleUpgrade}
            disabled={isChanging}
            className="underline font-medium hover:text-amber-800 dark:hover:text-amber-300 disabled:opacity-50"
          >
            {isChanging ? 'Changing...' : `Upgrade to ${TIER_DISPLAY[suggestedTier]}`}
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-center">
      <ArrowUpCircle size={36} className="mx-auto mb-3 text-blue-500" />
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
        Upgrade to unlock more {feature}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        You&apos;ve used {currentCount} of {limit === Infinity ? 'unlimited' : limit} {feature} on the {TIER_DISPLAY[tier]} plan.
      </p>
      <button
        onClick={handleUpgrade}
        disabled={isChanging}
        className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition inline-flex items-center gap-2 disabled:opacity-50"
      >
        {isChanging && <Loader2 size={16} className="animate-spin" />}
        {isChanging ? 'Changing plan...' : `Upgrade to ${TIER_DISPLAY[suggestedTier]}`}
      </button>
    </div>
  );
}
